import React, { useState } from 'react';
import {
  Alert, Button, Card, Space, Tag, Typography, Tooltip, Row, Col, Statistic,
} from 'antd';
import {
  CodeOutlined, ReloadOutlined, FullscreenOutlined, FullscreenExitOutlined,
  InfoCircleOutlined, WarningOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const QUICK_COMMANDS = [
  { label: 'Docker 狀態',    hint: 'docker ps -a --format "table {{.Names}}\\t{{.Status}}\\t{{.Ports}}"' },
  { label: '後端 Log',       hint: 'docker logs spo-backend --tail 50 -f' },
  { label: '前端 Log',       hint: 'docker logs spo-frontend --tail 30' },
  { label: 'Nginx Log',      hint: 'docker logs spo-nginx --tail 30' },
  { label: 'DB 連線數',      hint: "docker exec spo-postgres psql -U spo_user -d spo_db -c \"SELECT count(*) FROM pg_stat_activity;\"" },
  { label: '磁碟空間',       hint: 'df -h /' },
  { label: '記憶體用量',     hint: 'free -h' },
  { label: 'CPU 負載',       hint: 'top -bn1 | head -20' },
  { label: '進入後端 bash',  hint: 'docker exec -it spo-backend bash' },
  { label: '重啟後端',       hint: 'docker restart spo-backend' },
  { label: 'Users 數量',     hint: "docker exec spo-postgres psql -U spo_user -d spo_db -c \"SELECT role, count(*) FROM users GROUP BY role;\"" },
  { label: '登入記錄',       hint: "docker exec spo-postgres psql -U spo_user -d spo_db -c \"SELECT username, success, ip_address, created_at FROM login_logs ORDER BY created_at DESC LIMIT 20;\"" },
];

export default function TerminalPage({ currentUser }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [key, setKey] = useState(0);

  const isAllowed = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message="存取拒絕"
          description="此頁面僅限管理員（admin）和管理者（manager）使用。"
        />
      </div>
    );
  }

  return (
    <div style={{ height: fullscreen ? '100vh' : 'auto' }}>
      {!fullscreen && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                <CodeOutlined /> 系統終端機 (GoTTY)
              </Title>
              <Text type="secondary">直接在瀏覽器中操作後台 Linux 系統 / Docker</Text>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => setKey(k => k + 1)}>重新連線</Button>
              <Button icon={<FullscreenOutlined />} onClick={() => setFullscreen(true)}>全螢幕</Button>
            </Space>
          </div>

          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message="注意：此終端機直接操作伺服器，請謹慎使用，避免破壞性指令。"
          />

          {/* Quick commands */}
          <Card size="small" title={<><InfoCircleOutlined /> 常用指令（點擊複製）</>} style={{ marginBottom: 12 }}>
            <Space wrap>
              {QUICK_COMMANDS.map(cmd => (
                <Tooltip key={cmd.label} title={<code style={{ fontSize: 11 }}>{cmd.hint}</code>} placement="top">
                  <Tag
                    color="blue"
                    style={{ cursor: 'pointer', userSelect: 'none', fontSize: 12, padding: '3px 8px' }}
                    onClick={() => {
                      navigator.clipboard.writeText(cmd.hint)
                        .then(() => {
                          // Visual feedback handled by browser
                        })
                        .catch(() => {});
                    }}
                  >
                    {cmd.label}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 11 }}>
              點擊指令 Tag 可複製指令，在下方終端機貼上執行（Ctrl+V 或右鍵貼上）
            </Text>
          </Card>
        </>
      )}

      {/* Terminal iframe */}
      <div style={{
        position: fullscreen ? 'fixed' : 'relative',
        top: fullscreen ? 0 : 'auto',
        left: fullscreen ? 0 : 'auto',
        right: fullscreen ? 0 : 'auto',
        bottom: fullscreen ? 0 : 'auto',
        zIndex: fullscreen ? 9999 : 'auto',
        background: '#000',
        borderRadius: fullscreen ? 0 : 8,
        overflow: 'hidden',
        height: fullscreen ? '100vh' : 'calc(100vh - 340px)',
        minHeight: 400,
      }}>
        {fullscreen && (
          <Button
            icon={<FullscreenExitOutlined />}
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 10000,
              background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
            }}
            onClick={() => setFullscreen(false)}
          >
            離開全螢幕
          </Button>
        )}
        <iframe
          key={key}
          src="/terminal/"
          title="SPO System Terminal"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: '#000',
          }}
          allow="clipboard-read; clipboard-write"
        />
      </div>
    </div>
  );
}
